import type { ECUCataSelectOptionVariantInterface } from "../models/ECUCInterfaces";
import API from "./API";

export interface APIServiceFormat {
    isSuccess: boolean;
    data: any;
    message: string;
}

export interface FeatureFlagsResponse {
    signingAllowedModules: string[];
}

const APIServiceFormatTemplate: APIServiceFormat = {
    isSuccess: false,
    data: null,
    message: ""
}

export default class APIService {

    private readonly api: API;
    private readonly basePath: string = "/srp/form";
    private readonly baseHeader = {
        "Content-Type": "application/json",
        'Accept': `application/json`,
    }
    constructor(api: API) {
        this.api = api;
    }

    getFeatureFlags = async (): Promise<APIServiceFormat> => {
        const endpoint = `${this.basePath}/flags`;
        console.log("Fetching feature flags from:", endpoint);

        try {
            const response = await this.api.fetch(endpoint, {
                method: "GET",
                headers: this.baseHeader,
            });

            const data = await response.json().catch(() => {
                return "";
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status} - ${response.statusText} data: ${JSON.stringify(data)}`);
            }

            if (!data || typeof data !== "object" || !Array.isArray(data.signingAllowedModules)) {
                console.error("Invalid response format for feature flags:", data);
                throw new Error("Invalid response format for feature flags");
            }

            return {
                isSuccess: true,
                data: data as FeatureFlagsResponse,
                message: "Feature flags fetched successfully"
            };
        } catch (error: any) {
            console.error("Error fetching feature flags:", error);
            return {
                isSuccess: false,
                data: { signingAllowedModules: [] } as FeatureFlagsResponse,
                message: "Failed to fetch feature flags" + (error.message ? `: ${error.message}` : "")
            };
        }
    }

    getProgramOptions = async (programIds: string[]): Promise<APIServiceFormat> => {

        console.log("Fetching program options for IDs:", programIds);

        const endpoint = `${this.basePath}/options/programs`;
        console.log("Making API call to:", endpoint);
        try {
            const response = await this.api.fetch(endpoint, {
                method: "POST",
                headers: this.baseHeader,
                body: JSON.stringify({ programIds }),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (!Array.isArray(data) ||
                data.some(option => typeof option.label !== 'string' || typeof option.id !== 'number')) {
                console.error("Invalid response format for program options:", data);
                throw new Error("Invalid response format for program options");
            }

            let formattedData = data.map((option: { label: string; id: string }) => ({
                label: option.label,
                value: option.id
            }));

            formattedData = formattedData.filter(option =>
                !["V801-demo", "joeydemo2", "joeytestprogram", "joeytestprogram2", "P702 MCA ICE/FHEV", "U717", "U718", "U611", "U625", "platformFeatureProgramCode_80778_1", "CDX707"].includes(option.label) &&
                !option.label.toLowerCase().includes("test") &&
                !option.label.toLowerCase().includes("demo")
            );

            return {
                isSuccess: true,
                data: formattedData,
                message: "Program options fetched successfully"
            };
        } catch (error: any) {
            console.error("Error fetching program options:", error);
            return {
                isSuccess: false,
                data: [],
                message: "Failed to fetch program options" + (error.message ? `: ${error.message}` : "")
            };
        }
    }

    getProgramData = async (programIds: string[]): Promise<APIServiceFormat> => {
        console.log("Fetch program data for IDs:", programIds);



        try {
            const endpoints = `${this.basePath}/selections/programs`;
            const requestBody = {
                programIds
            };
            console.log("Making API call to:", endpoints, "with body:", requestBody);

            const response = await this.api.fetch(endpoints, {
                method: "POST",
                headers: this.baseHeader,
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (!Array.isArray(data)) {
                console.error("Invalid response format for program data:", data);
                throw new Error("Invalid response format for program data");
            }

            return {
                isSuccess: true,
                data: data,
                message: "Program data fetched successfully"
            };
        } catch (error: any) {
            console.error("Error fetching program data:", error);
            return {
                isSuccess: false,
                data: [],
                message: "Failed to fetch program data" + (error.message ? `: ${error.message}` : "")
            };
        }
    }


    getModuleOptions = async (programIds: string[]): Promise<APIServiceFormat> => {
        console.log("Fetching module options for program IDs:", programIds);
        try {
            const endpoint = `${this.basePath}/options/modules`;
            const requestBody = {
                programIds
            };
            console.log("Making API call to:", endpoint, "with body:", requestBody);

            const response = await this.api.fetch(endpoint, {
                method: "POST",
                headers: this.baseHeader,
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (!Array.isArray(data) ||
                data.some(option => typeof option.label !== 'string' || typeof option.id !== 'number')) {
                console.error("Invalid response format for module options:", data);
                throw new Error("Invalid response format for module options");
            }

            const formattedData = data.map((option: { label: string; id: string }) => ({
                label: option.label,
                value: option.id
            }));

            return {
                isSuccess: true,
                data: formattedData,
                message: "Module options fetched successfully"
            };

        } catch (error: any) {
            console.error("Error fetching module options:", error);
            return {
                isSuccess: false,
                data: [],
                message: "Failed to fetch module options" + (error.message ? `: ${error.message}` : "")
            };
        }


    }

    getModuleData = async (programIds: string[], moduleId: string): Promise<APIServiceFormat> => {
        console.log("Fetching module data for program IDs:", programIds, "and module ID:", moduleId);

        const endpoint = `${this.basePath}/selections/modules`;
        const requestBody = {
            programIds,
            moduleId
        };
        console.log("Making API call to:", endpoint, "with body:", requestBody);

        try {

            const response = await this.api.fetch(endpoint, {
                method: "POST",
                headers: this.baseHeader,
                body: JSON.stringify(requestBody),
            });

            const data = await response.json().catch(() => {
                return "";
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status} - ${response.statusText} data: ${JSON.stringify(data)}`);
            }
            if (!data || typeof data !== 'object') {
                console.error("Invalid response format for module data:", data);
                throw new Error("Invalid response format for module data");
            }
            console.log("Module data fetched successfully:", data);
            return {
                isSuccess: true,
                data: data,
                message: "Module data fetched successfully"
            };

        } catch (error: any) {
            console.error("Error fetching module data:", error);
            return {
                isSuccess: false,
                data: [],
                message: "Failed to fetch module data" + (error.message ? `: ${error.message}` : "")
            };
        }
    }

    getMachineOptions = async (programIds: string[], moduleId: string): Promise<APIServiceFormat> => {
        console.log("Fetching machine options for program IDs:", programIds, "and module ID:", moduleId);

        try {
            const endpoint = `${this.basePath}/options/machines`;
            const requestBody = {
                programIds,
                moduleId
            };
            console.log("Making API call to:", endpoint, "with body:", requestBody);

            const response = await this.api.fetch(endpoint, {
                method: "POST",
                headers: this.baseHeader,
                body: JSON.stringify(requestBody),
            });
            const data = await response.json().catch(() => {
                return "";
            }); 
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status} - ${response.statusText} data: ${JSON.stringify(data)}`);
            }
            if (!Array.isArray(data) ||
                data.some(option => typeof option.label !== 'string' || typeof option.id !== 'number')) {
                console.error("Invalid response format for machine options:", data);
                throw new Error("Invalid response format for machine options");
            }
            const formattedData = data.map((option: { label: string; id: string }) => ({
                label: option.label,
                value: option.id
            }));
            console.log("Machine options fetched successfully:", formattedData);
            return {
                isSuccess: true,
                data: formattedData,
                message: "Machine options fetched successfully"
            };
        } catch (error: any) {
            console.error("Error fetching machine options:", error);
            return {
                isSuccess: false,
                data: [],
                message: "Failed to fetch machine options" + (error.message ? `: ${error.message}` : "")
            };
        }
    }

    getMachineData = async (programIds: string[], moduleId: string, machineIds: string[]): Promise<APIServiceFormat> => {

        console.log("Fetching machine data for program IDs:", programIds, "module ID:", moduleId, "and machine IDs:", machineIds);
        const endpoint = `${this.basePath}/selections/machines`;
        const requestBody = {
            programIds,
            moduleId,
            machineIds
        };
        console.log("Making API call to:", endpoint, "with body:", requestBody);
        try {
            const response = await this.api.fetch(endpoint, {
                method: "POST",
                headers: this.baseHeader,
                body: JSON.stringify(requestBody),
            });

            const data = await response.json().catch(() => {
                return "";
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status} - ${response.statusText} data: ${JSON.stringify(data)}`);
            }
            if (!Array.isArray(data)) {
                console.error("Invalid response format for machine data:", data);
                throw new Error("Invalid response format for machine data");
            }
            console.log("Machine data fetched successfully:", data);
            return {
                isSuccess: true,
                data: data,
                message: "Machine data fetched successfully"
            };

        } catch (error: any) {
            console.error("Error fetching machine data:", error);
            return {
                isSuccess: false,
                data: [],
                message: "Failed to fetch machine data" + (error.message ? `: ${error.message}` : "")
            };
        }

    }


    getVariantOptions = async (programIds: string[], moduleId: string, machineIds: string[]): Promise<APIServiceFormat> => {
        try {

            console.log("Fetching variant options for program IDs:", programIds, "module ID:", moduleId, "and machine IDs:", machineIds);

            const endpoint = `${this.basePath}/options/variants`;


            // Loop through machineList and make API calls to fetch variant options for each machine
            const variantOptions: { label: string; value: string, machineId: string }[] = [];
            for (const machineId of machineIds) {

                const requestBody = {
                    programIds,
                    moduleId,
                    machineIds: [machineId] // Send only the current machine ID
                };
                console.log("Making API call to:", endpoint, "with body:", requestBody);
                const response = await this.api.fetch(`${endpoint}`, {
                    method: "POST",
                    headers: this.baseHeader,
                    body: JSON.stringify(requestBody), // Send only the current machine ID
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
                }
                const data = await response.json().catch(() => {
                    return "";
                });
                if (!Array.isArray(data) ||
                    data.some(option => typeof option.label !== 'string' || typeof option.id !== 'number')) {
                    console.error("Invalid response format for variant options:", data);
                    throw new Error("Invalid response format for variant options");
                }
                const newOptions = data.map((option: { label: string; id: string }) => ({
                    label: option.label,
                    value: option.id,
                    machineId: machineId // Include machineId in the option
                }));
                variantOptions.push(...newOptions);
                
                
            }

            return {
                isSuccess: true,
                data: variantOptions,
                message: "Variant options fetched successfully"
            }

        } catch (error: any) {
            console.error("Error fetching variant options:", error);
            return {
                isSuccess: false,
                data: [],
                message: "Failed to fetch variant options" + (error.message ? `: ${error.message}` : "")
            };
        }
    }

    getVariantData = async (programIds: string[], moduleId: string, machineIds: string[], variantIds: string[]): Promise<APIServiceFormat> => {
        console.log("Fetching variant data for program IDs:", programIds, "module ID:", moduleId, "machine IDs:", machineIds, "and variant IDs:", variantIds);

       try {
            const endpoint = `${this.basePath}/selections/variants`;
            const requestBody = {
                programIds,
                moduleId,
                machineIds,
                variantIds: variantIds // Send all variant IDs
            };
            console.log("Making API call to:", endpoint, "with body:", requestBody);

            const response = await this.api.fetch(endpoint, {
                method: "POST",
                headers: this.baseHeader,
                body: JSON.stringify(requestBody),
            });

            const data = await response.json().catch(() => {
                return "";
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status} - ${response.statusText} data: ${JSON.stringify(data)}`);
            }
            if (!Array.isArray(data)) {
                console.error("Invalid response format for variant data:", data);
                throw new Error("Invalid response format for variant data");
            }
            console.log("Variant data fetched successfully:", data);
            return {
                isSuccess: true,
                data: data,
                message: "Variant data fetched successfully"
            };

        } catch (error: any) {
            console.error("Error fetching variant data:", error);
            return {
                isSuccess: false,
                data: [],
                message: "Failed to fetch variant data" + (error.message ? `: ${error.message}` : "")
            };
       }
    }

    getClusterOptions = async (programIds: string[], moduleId: string, machineIds: string[], 
        variantIds: ECUCataSelectOptionVariantInterface[]): Promise<APIServiceFormat> => {

        // loop through variantIds and make API calls to fetch cluster options for each variant
        try {
            console.log("Fetching cluster options for program IDs:", programIds, "module ID:", moduleId, "machine IDs:", machineIds, "and variant IDs:", variantIds);

            const endpoint = `${this.basePath}/options/clusters`;

            const clusterOptions: { label: string; value: string, variantId: string }[] = [];
            for (const variant of variantIds) {
                const variantId = variant.value; 
                const machineId = variant.machineId; 
                const requestBody = {
                    programIds,
                    moduleId,
                    machineIds: [machineId], // Send only the current machine ID
                    variantIds: [variantId] // Send only the current variant ID
                };

                const response = await this.api.fetch(`${endpoint}`, {
                    method: "POST",
                    headers: this.baseHeader,
                    body: JSON.stringify(requestBody), // Send only the current variant ID
                });

                const data = await response.json().catch(() => {
                    return "";
                });
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status} - ${response.statusText} data: ${JSON.stringify(data)}`);
                }
                if (!Array.isArray(data) ||
                    data.some(option => typeof option.label !== 'string' || typeof option.id !== 'number')) {
                    console.error("Invalid response format for cluster options:", data);
                    throw new Error("Invalid response format for cluster options"); 
                    }
                const newOptions = data.map((option: { label: string; id: string }) => ({
                    label: option.label,
                    value: option.id,
                    variantId: variantId, // Include variantId in the option
                    machineId: machineId // Include machineId in the option
                }));
                clusterOptions.push(...newOptions);

            }

            return {
                isSuccess: true,
                data: clusterOptions,
                message: "Cluster options fetched successfully"
            }

        } catch (error: any) {
            console.error("Error fetching cluster options:", error);
            return {
                isSuccess: false,
                data: [],
                message: "Failed to fetch cluster options" + (error.message ? `: ${error.message}` : "")
            };
        }
    }

    getClusterData = async (programIds: string[], moduleId: string, machineIds: string[], variantIds: string[], clusterIds: string[]): Promise<APIServiceFormat> => {
        console.log("Fetching cluster data for program IDs:", programIds, "module ID:", moduleId, "machine IDs:", machineIds, "variant IDs:", variantIds, "and cluster IDs:", clusterIds);

        const endpoint = `${this.basePath}/selections/clusters`;
        const requestBody = {
            programIds,
            moduleId,
            machineIds,
            clusterIds,
            variantIds: variantIds // Send all variant IDs
        };
        console.log("Making API call to:", endpoint, "with body:", requestBody);

        try {
            const response = await this.api.fetch(endpoint, {
                method: "POST",
                headers: this.baseHeader,
                body: JSON.stringify(requestBody),
            });

            const data = await response.json().catch(() => {
                return "";
            });

            if (!response.ok) {
                const errorMessage = `HTTP error! status: ${response.status} - ${response.statusText} data: ${JSON.stringify(data)}`;
                console.error(errorMessage);
                throw new Error(`HTTP error! status: ${response.status} - ${response.statusText} data: ${JSON.stringify(data)}`);
            }
            if (!Array.isArray(data)) {
                console.error("Invalid response format for cluster data:", data);
                throw new Error("Invalid response format for cluster data");
            }
            console.log("Cluster data fetched successfully:", data);
            return {
                isSuccess: true,
                data: data,
                message: "Cluster data fetched successfully"
            };

        } catch (error: any) {
            console.error("Error fetching cluster data:", error);
            return {
                isSuccess: false,
                data: [],
                message: "Failed to fetch cluster data" + (error.message ? `: ${error.message}` : "")
            };
        }
    }

    getClusterDataAlternative = async (clusterId: string): Promise<APIServiceFormat> => {
        console.log("Fetching cluster data (alternative) for cluster ID:", clusterId);

        const param = new URLSearchParams({ clusterId });
        const endpoint = `${this.basePath}/clusters?${param}`;

        try {
            const response = await this.api.fetch(endpoint, {
                method: "GET",
                headers: this.baseHeader,
            });

            const data = await response.json();

            if (!response.ok) {
                const errorMessage = `HTTP error! status: ${response.status} - ${response.statusText} data: ${JSON.stringify(data)}`;
                console.error(errorMessage);
                throw new Error(`HTTP error! status: ${response.status} - ${response.statusText} data: ${JSON.stringify(data)}`);
            }

            if (!data || typeof data !== 'object') {
                console.error("Invalid response format for cluster data:", data);
                throw new Error("Invalid response format for cluster data");
            }

            console.log("Cluster data fetched successfully:", data);
            return {
                isSuccess: true,
                data: data,
                message: "Cluster data fetched successfully"
            };

        } catch (error: any) {
            console.error("Error fetching cluster data:", error);
            return {
                isSuccess: false,
                data: [],
                message: "Failed to fetch cluster data" + (error.message ? `: ${error.message}` : "")
            };
        }
    }

    getSigningDirectoryOptions = async ({
        modelYearProgramCodes,
        moduleName,
        machineName,
        canDiagRxId,
        variantName,
    }: {
        modelYearProgramCodes: string;
        moduleName: string;
        machineName: string;
        canDiagRxId: string;
        variantName: string;
    }): Promise<APIServiceFormat> => {
        console.log("Fetching signing directory options for modelYearProgramCodes:", modelYearProgramCodes, "moduleName:", moduleName, "machineName:", machineName, "canDiagRxId:", canDiagRxId, "variantName:", variantName);

        const pageSize = 100;
        const endpoint = `${this.basePath}/clusters/signing-directory/options`;
        const signingIds: string[] = [];

        try {
            let page = 0;
            let totalElements = Infinity;

            while (signingIds.length < totalElements) {
                const params = new URLSearchParams({
                    size: String(pageSize),
                    page: String(page),
                    modelYearProgramCodes,
                    moduleName,
                    machineName,
                    canDiagRxId,
                    variantName,
                });

                const response = await this.api.fetch(`${endpoint}?${params}`, {
                    method: "GET",
                    headers: this.baseHeader,
                });

                const data = await response.json().catch(() => null);

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status} - ${response.statusText} data: ${JSON.stringify(data)}`);
                }

                if (!data || !Array.isArray(data.content) || typeof data.totalElements !== 'number') {
                    console.error("Invalid response format for signing directory options:", data);
                    throw new Error("Invalid response format for signing directory options");
                }

                totalElements = data.totalElements;
                console.log("Content", data.content, "Total Elements:", totalElements);
                const ids = data.content.flatMap((item: { signingIds: string[] }) => item.signingIds);
                signingIds.push(...ids);

                if (data.last || signingIds.length >= totalElements) {
                    break;
                }

                page++;
            }

            console.log(`Fetched ${signingIds.length} signing directory options`);
            return {
                isSuccess: true,
                data: signingIds,
                message: "Signing directory options fetched successfully"
            };
        } catch (error: any) {
            console.error("Error fetching signing directory options:", error);
            return {
                isSuccess: false,
                data: [],
                message: "Failed to fetch signing directory options" + (error.message ? `: ${error.message}` : "")
            };
        }
    }

    getCss = async (id: string): Promise<string> => {        
        const endpoint = `${this.basePath}/css/${id}`;
        
        try {
            const response = await this.api.fetch(endpoint, {
                method: "GET",
                headers: this.baseHeader,
            });

            const data = await response.json().catch(() => {
                return "";
            });
            
            if (!response.ok) {
                const errorMessage = `HTTP error! status: ${response.status} - ${response.statusText} data: ${JSON.stringify(data)}`;
                console.error(errorMessage);
                throw new Error(errorMessage);
            }

            if (!data || typeof data !== 'string') {
                console.error("Invalid response format for CSS PJ Key:", data);
                throw new Error("Invalid response format for CSS PJ Key");
            }
            return data;
        } catch (error: any) {
            console.error("Error fetching CSS PJ Key:", error);
            throw error;
        }
    }

    getSignFileMetadata = async (containerUrl: string): Promise<APIServiceFormat> => {
        const endpoint = `/cluster/signfile/metadata`;

        // POST
        const requestBody = {
            containerUrl
        };
        
        try {
            console.log("Fetching sign file metadata for container URL:", containerUrl, "with endpoint:", endpoint, "and request body:", requestBody);
            const response = await this.api.fetch(endpoint, {
                method: "POST",
                headers: this.baseHeader,
                body: JSON.stringify(requestBody),
            });

            const data = await response.json().catch(() => {
                return "";
            });
            
            if (!response.ok) {
                const errorMessage = `HTTP error! status: ${response.status} - ${response.statusText} data: ${JSON.stringify(data)}`;
                console.error(errorMessage);
                throw new Error(errorMessage);
            }

            if (!data || !Array.isArray(data)) {
                console.error("Invalid response format for sign file metadata:", data);
                throw new Error("Invalid response format for sign file metadata");
            }
            console.log("Sign file metadata fetched successfully");
            return {
                isSuccess: true,
                data: data,
                message: "Sign file metadata fetched successfully"
            };
        } catch (error: any) {
            console.error("Error fetching sign file metadata:", error);
            return {
                isSuccess: false,
                data: null,
                message: "Failed to fetch sign file metadata" + (error.message ? `: ${error.message}` : "")
            };
        }

    }

    
}